import React, { useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useProcessing } from '../../context/ProcessingContext';
import DataTabs from '../Grid/DataTabs';



 const calculateDefaultInterval = (data: number[][]): number => {
    // Get valid data (exclude dummy/null values)
    const DUMMY_VALUE = -9999;
    const validData = data.flat().filter((value): value is number => 
        typeof value === 'number' && value !== DUMMY_VALUE && !isNaN(value)
    );
    
    if (validData.length === 0) {
        return 1.0; // Default if no valid data
    }
    
    // Calculate data range
    const dataRange = Math.max(...validData) - Math.min(...validData);
    
    // Get order of magnitude
    const magnitude = Math.floor(Math.log10(dataRange));
    
    // Calculate base interval (1, 2, or 5 times power of 10)
    const baseInterval = Math.pow(10, magnitude - 1);
    
    // Adjust interval to be 1, 2, or 5 times the base
    if (dataRange / baseInterval > 50) {
        return baseInterval * 5;
    } else if (dataRange / baseInterval > 20) {
        return baseInterval * 2;
    } else {
        return baseInterval;
    }
 };

 const isLatLongData = (geoMetadata: any): boolean => {
  return geoMetadata?.original_crs === 'latlong';
 };

 const calculateMinMaxIntervals = (data: number[][]): { minInterval: number, maxInterval: number } => {
    const DUMMY_VALUE = -9999;
    const validData = data.flat().filter((value): value is number => 
        typeof value === 'number' && value !== DUMMY_VALUE && !isNaN(value)
    );
    
    if (validData.length === 0) {
        return { minInterval: 1, maxInterval: 10 };
    }
    
    const min = Math.min(...validData);
    const max = Math.max(...validData);
    const range = max - min;
    
    // Calculate default interval
    const defaultInterval = calculateDefaultInterval(data);
    
    // Set min interval to 1/10th of default and max to 10x default
    const minInterval = defaultInterval / 10;
    const maxInterval = defaultInterval * 10;
    
    return { minInterval, maxInterval };
 };

 const COLOR_SCHEMES = [
  { value: 'terrain', label: 'Terrain' },
  { value: 'viridis', label: 'Viridis' },
  { value: 'plasma', label: 'Plasma' },
  { value: 'magma', label: 'Magma' }
 ];

export const ContourVisualization: React.FC = () => {

  const { state, dispatch } = useProcessing();
  const [isUpdating, setIsUpdating] = useState(false);

   const [contourConfig, setContourConfig] = useState({
    interval: calculateDefaultInterval(state.interpolatedData?.ml?.data || []),
    colorScheme: 'terrain',
    opacity: 0.7,
    thickness: 0.5,
    labels: true,
    showPoints: false,
    invertColorScheme: false,
    backgroundColor: '#ffffff'
   });

  if (!state.interpolatedData?.ml || !state.rawData) {
    return (
      <div className="panel-section">
        <p className="empty-state">Please complete the interpolation step first.</p>
      </div>
    );
   }
  
  const handleMapTransition = async () => {
    if (!state.interpolatedData?.ml?.data || isUpdating) return;
    
    setIsUpdating(true);
    try {
      const response = await fetch('/api/contours-geojson', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interpolatedData: state.interpolatedData.ml.data,
          contourConfig,
          geographicMetadata: state.rawData?.geographic_metadata
        })
      });

      if (!response.ok) throw new Error(await response.text());

      const geoJSON = await response.json();
      dispatch({ type: 'SET_CONTOUR_GEOJSON', payload: geoJSON });
      dispatch({ type: 'SET_VIEW_STEP', payload: 'map' });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to generate map data'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const updateContour = async (newConfig: Partial<typeof contourConfig>) => {
    if (!state.interpolatedData?.ml?.data) return;
    
    const updatedConfig = { ...contourConfig, ...newConfig };
    setContourConfig(updatedConfig);

    try {
      const response = await fetch('/api/contour/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interpolatedData: state.interpolatedData.ml.data,
          config: updatedConfig,
          geographicMetadata: state.rawData?.geographic_metadata,
          originalPoints: state.rawData?.data 
        })
      });

      if (!response.ok) throw new Error(await response.text());

      const result = await response.json();
      
      if (!state.interpolatedData?.ml) return;
      
      dispatch({
        type: 'SET_INTERPOLATED_DATA',
        payload: {
          ...state.interpolatedData,
          ml: {
            ...state.interpolatedData.ml,
            plot: result.plot
          }
        }
      });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to update contour'
      });
    }
  };

  const { minInterval, maxInterval } = calculateMinMaxIntervals(state.interpolatedData.ml.data);
  const stepInterval = minInterval;
  
  return (
    <div className="panel-section">
      <PanelGroup direction="horizontal">
        <Panel defaultSize={65} minSize={30}>
          <div className="grid-main-content">
           <div className="data-visualization">
             <img 
               src={`data:image/png;base64,${state.interpolatedData.ml.plot}`}
               alt="Contour visualization"
               className="preview-image"
             />
           </div>

           <div className="button-group">
             <button
               className="map-button"
               onClick={handleMapTransition}
               disabled={isUpdating || !isLatLongData(state.rawData.geographic_metadata)}
             >
               {isUpdating ? (
                 <>
                   <span className="processing-spinner" />
                   <span>Preparing Map...</span>
                 </>
               ) : 'Interactive Map'}
             </button>

             <button
               className="export-button"
               onClick={() => dispatch({ type: 'SET_VIEW_STEP', payload: 'export' })}
             >
               Export Data
             </button>
           </div>
         </div>
        </Panel>

        <PanelResizeHandle className="resize-handle" />

        <Panel defaultSize={35} minSize={20}>
          <div className="grid-sidebar">
            <div className="control-group">
              <h3>Contour Aesthetics</h3>
              
              {/* Color Scheme */}
              <div className="control-item">
                <label>Color Scheme</label>
                <div className="scheme-buttons">
                  {COLOR_SCHEMES.map(scheme => (
                    <button 
                      key={scheme.value}
                      className={`scheme-button ${contourConfig.colorScheme === scheme.value ? 'active' : ''}`}
                      onClick={() => updateContour({ colorScheme: scheme.value })}
                    >
                      {scheme.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Invert Color Scheme */}
              <div className="control-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={contourConfig.invertColorScheme}
                    onChange={(e) => updateContour({ invertColorScheme: e.target.checked })}
                  />
                  Invert Color Scheme
                </label>
              </div>

              {/* Contour Interval */}
              <div className="control-item">
                <label>Contour Interval</label>
                <div className="control-input">
                  <input
                    type="range"
                    min={minInterval}
                    max={maxInterval}
                    step={stepInterval}
                    value={contourConfig.interval}
                    onChange={(e) => updateContour({ interval: Number(e.target.value) })}
                  />
                  <span className="value">{contourConfig.interval.toFixed(2)}</span>
                </div>
              </div>

              {/* Line Thickness */}
              <div className="control-item">
                <label>Line Thickness</label>
                <div className="control-input">
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.1"
                    value={contourConfig.thickness}
                    onChange={(e) => updateContour({ thickness: Number(e.target.value) })}
                  />
                  <span className="value">{contourConfig.thickness.toFixed(1)}</span>
                </div>
              </div>

              {/* Opacity */}
              <div className="control-item">
                <label>Opacity</label>
                <div className="control-input">
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={contourConfig.opacity}
                    onChange={(e) => updateContour({ opacity: Number(e.target.value) })}
                  />
                  <span className="value">{contourConfig.opacity.toFixed(1)}</span>
                </div>
              </div>

              {/* Background Color */}
              <div className="control-item">
                <label>Background Color</label>
                <div className="scheme-buttons">
                  <button
                    className={`scheme-button ${contourConfig.backgroundColor === '#ffffff' ? 'active' : ''}`}
                    onClick={() => updateContour({ backgroundColor: '#ffffff' })}
                  >
                    White
                  </button>
                  <button
                    className={`scheme-button ${contourConfig.backgroundColor === '#f4f4f4' ? 'active' : ''}`}
                    onClick={() => updateContour({ backgroundColor: '#f4f4f4' })}
                  >
                    L. Gray
                  </button>
                  <button
                    className={`scheme-button ${contourConfig.backgroundColor === '#999999' ? 'active' : ''}`}
                    onClick={() => updateContour({ backgroundColor: '#999999' })}
                  >
                    D. Gray
                  </button>
                  <button
                    className={`scheme-button ${contourConfig.backgroundColor === '#2d2d2d' ? 'active' : ''}`}
                    onClick={() => updateContour({ backgroundColor: '#2d2d2d' })}
                  >
                    Dark
                  </button>
                </div>
              </div>

              {/* Show Labels */}
              <div className="control-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={contourConfig.labels}
                    onChange={(e) => updateContour({ labels: e.target.checked })}
                  />
                  Show Contour Labels
                </label>
              </div>

              {/* Show Original Points */}
              <div className="control-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={contourConfig.showPoints}
                    onChange={(e) => updateContour({ showPoints: e.target.checked })}
                  />
                  Show Original Points
                </label>
              </div>
            </div>

            {/* Data Information */}
            <div className="control-group">
              <h3>Data Information</h3>
              <DataTabs 
                histogram={state.interpolatedData.ml.histogram}
                stats={state.interpolatedData.ml.stats}
                geoMetadata={state.rawData.geographic_metadata}
              />
            </div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
};

